// Number + unit entry for byte-valued contract fields.
//
// These fields are stored as raw bytes, and typing 10995116277760 for "10 TB"
// is both unreadable and dangerous: one digit too many sells ten times the
// agreed volume and nothing about the number makes that visible. Here the
// operator enters the figure in the unit the contract uses, and the resolved
// size is echoed underneath so the committed value can be proof-read at a
// glance.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BYTE_UNITS, amountText, bytesFrom, splitBytes } from '../lib/bytes';
import type { ByteUnit } from '../lib/bytes';
import { formatBytes } from '../lib/format';
import type { Language } from '../i18n';

export function ByteInput({
  /** The value in BYTES as a string; '' means empty/not set. */
  value,
  onChange,
  fieldProps,
  defaultUnit = 'GB',
  disabled,
}: {
  value: string;
  onChange: (bytes: string) => void;
  fieldProps?: Record<string, unknown>;
  defaultUnit?: ByteUnit;
  disabled?: boolean;
}) {
  const { i18n, t } = useTranslation();
  const [unit, setUnit] = useState<ByteUnit>(() => splitBytes(Number(value), defaultUnit).unit);
  const [text, setText] = useState<string>(() => amountText(value, splitBytes(Number(value), defaultUnit).unit));

  // Adopt values that changed from the OUTSIDE (a different plan selected, a
  // reset). Comparing against what we last emitted keeps the operator's own
  // typing untouched — otherwise "1.5" would be rewritten to "1536 MB"
  // mid-keystroke.
  const emitted = useRef(value);
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    const s = splitBytes(Number(value), defaultUnit);
    setUnit(s.unit);
    setText(amountText(value, s.unit));
  }, [value, defaultUnit]);

  const emit = (nextText: string, nextUnit: ByteUnit) => {
    const bytes = bytesFrom(nextText, nextUnit);
    emitted.current = bytes;
    onChange(bytes);
  };

  const bytes = Number(bytesFrom(text, unit));
  const preview = text.trim() === '' ? '' : formatBytes(bytes, i18n.language as Language);

  return (
    <>
      <span className="row" style={{ gap: 6, alignItems: 'center' }}>
        <input
          className="input"
          {...fieldProps}
          type="number"
          min="0"
          step="any"
          disabled={disabled}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            emit(e.target.value, unit);
          }}
        />
        <select
          className="select"
          style={{ width: 'auto' }}
          aria-label={t('admin.fields.unit')}
          disabled={disabled}
          value={unit}
          onChange={(e) => {
            const u = e.target.value as ByteUnit;
            setUnit(u);
            emit(text, u);
          }}
        >
          {BYTE_UNITS.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
      </span>
      {preview ? (
        <p className="field__hint" style={{ margin: '2px 0 0' }}>
          {t('admin.fields.resolvesTo', { size: preview, bytes: bytes.toLocaleString(i18n.language) })}
        </p>
      ) : null}
    </>
  );
}
